import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form';
import Alert from 'antd/lib/alert';
import Text from 'antd/lib/typography/Text';
import { TriageViewType, TRY_REQUEST_ISSUE_FIX, tryRequestIssueFix } from '@/actions/triage';
import { AppStoreType, AutomationType, IssueEntity } from '@/types';
import { RootState } from '@/reducers/reducers';
import { selectAppStoreType, selectAutomationsByType, selectIsAutomationEnabled } from '@/selectors/selectors';
import { antGrey } from '@/components/styles/globalStyles';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import AutomationSettingsBanner from '@/components/sharedComponents/AutomationSettingsBanner';
import { TriageIssueCard } from './TriageIssueCard';
import { FixRequestFormValues, useFixRequestForm } from './FixRequestForm';
import { automationDisplayNameForAutomationId } from '@/helpers/displayHelpers';

interface RequestFixModalProps {
    open: boolean;
    onClose: () => void;
    orgId: string;
    appId: string;
    issue: IssueEntity;
    viewType: TriageViewType;
}

export const RequestFixModal: React.FC<RequestFixModalProps> = ({ open, onClose, orgId, appId, issue, viewType }) => {
    const dispatch = useDispatch();
    const [form] = Form.useForm<FixRequestFormValues>();

    // The work item is optional when the auto-create-PM-ticket automation will supply a ticket itself.
    const createTicketAutomationId = useSelector(
        (state: RootState) =>
            selectAutomationsByType(state, appId, AutomationType.AutoCreatePMTicketOnTriageIssueFixRequest)?.[0]?.id,
    );
    const isAutoCreateTicketEnabled = useSelector((state: RootState) =>
        selectIsAutomationEnabled(state, appId, createTicketAutomationId),
    );

    const {
        nextVersion,
        selectedVersion,
        sourceTicket,
        fields,
        seedOnOpen,
        buildFormFieldsValues,
        buildWorkItems,
        onOpenChange,
    } = useFixRequestForm({ appId, orgId, form, issue, workItemOptional: isAutoCreateTicketEnabled });

    const isSaving = useIsLoading(TRY_REQUEST_ISSUE_FIX, issue.id);
    const saveError = useError(TRY_REQUEST_ISSUE_FIX, issue.id);
    const saveSucceeded = useUpdated(TRY_REQUEST_ISSUE_FIX, issue.id);

    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const automationName = automationDisplayNameForAutomationId(
        AutomationType.AutoCreatePMTicketOnTriageIssueFixRequest,
        appStoreType ?? AppStoreType.None,
    );

    React.useEffect(() => {
        if (saveSucceeded) {
            form.resetFields();
            onClose();
        }
    }, [saveSucceeded]);

    const onSubmit = (values: FixRequestFormValues) => {
        const version = values.version ?? nextVersion;
        if (!version) {
            return;
        }
        dispatch(
            tryRequestIssueFix({
                orgId,
                appId,
                issueId: issue.id,
                version,
                formFieldsValues: buildFormFieldsValues(values),
                workItems: buildWorkItems(),
                viewType,
            }),
        );
    };

    return (
        <Modal
            title="Request approval for a fix"
            open={open}
            onCancel={onClose}
            onOk={() => form.submit()}
            okText="Submit request"
            okButtonProps={{ disabled: !selectedVersion }}
            confirmLoading={isSaving}
            afterOpenChange={(isOpen) => {
                onOpenChange(isOpen);
                if (isOpen) {
                    seedOnOpen();
                }
            }}
            destroyOnClose
            width={600}
        >
            <Text color={antGrey.grey8}>
                Choose the issue you&apos;d like to pull in as a fix for this release. You can optionally include
                details about why the fix is needed.
            </Text>
            <Form
                form={form}
                layout="vertical"
                onFinish={onSubmit}
                requiredMark
                preserve={false}
                style={{ marginTop: 24 }}
            >
                {saveError && <Alert type="error" message={saveError} showIcon style={{ marginBottom: 16 }} />}
                {fields}
                <Form.Item label="Associated issue" required>
                    <TriageIssueCard issue={issue} orgId={orgId} preview viewType={viewType} />
                </Form.Item>
            </Form>
            {createTicketAutomationId && (
                <AutomationSettingsBanner
                    automationId={createTicketAutomationId}
                    appId={appId}
                    orgId={orgId}
                    isEnabled={isAutoCreateTicketEnabled}
                    bannerStyle={{ marginTop: 15, padding: 12 }}
                    bannerText={
                        sourceTicket ?
                            <Text style={{ flexGrow: 1, marginRight: 15 }}>
                                This issue&apos;s source ticket <b>{sourceTicket.identifier}</b> is associated as the
                                work item for this fix
                                {isAutoCreateTicketEnabled ? ', so no new ticket will be created.' : '.'}
                            </Text>
                        : isAutoCreateTicketEnabled ?
                            <Text style={{ flexGrow: 1, marginRight: 15 }}>
                                The &apos;{automationName}&apos; automation is <b>enabled</b>. A ticket will be created
                                and associated automatically unless you associate one here.
                            </Text>
                        :   <Text type="secondary" style={{ flexGrow: 1, marginRight: 15 }}>
                                The &apos;{automationName}&apos; automation is <b>disabled</b>. You must associate at
                                least one work item.
                            </Text>

                    }
                />
            )}
        </Modal>
    );
};
